import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { getDayCache, setDayCache } from "../lib/dayCache";
import { PORTAL_PLATFORMS } from "../lib/platforms";
import { ZoneChip, LageBadge, Input, MockBadge, Spinner } from "../components/ui";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Search, FileDown, X, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { isLowConfidence } from "../lib/mvConfidence";
import {
  buildMarketOptions,
  marketOptionMatchesSelection,
  normalizeMarketSearch,
} from "../lib/marketMeta";
import {
  FilterCell,
  MarketMultiSelect,
  Metric,
  RangeCell,
  percentile99,
  PRICE_SANITY,
  SIZE_SANITY,
} from "../components/filters";

// HKL-Strategie product spec (CLAUDE.md/HKL-Strategie.md): 1-Zimmer, 14–40m²
// (the --hkl CLI's browsing band — 20–35m² is its narrower "sweet spot" within
// that), €70–170k, Faktor 18–22, AND MRG-checked: baujahr<1953 (VOLLANWENDUNG)
// AND baujahr>=1953 (UNKNOWN — Förderungsstatus unresolved, see invest_config.
// mrg_regime()'s docstring) both need profitability recomputed at the
// Richtwert-capped rent to still be cashflow-positive — "baujahr >= 1953"
// alone is NOT a confirmed exemption, it's simply unverified either way.
function isHklMatch(l) {
  const rooms = parseFloat(String(l.rooms ?? "").replace(",", "."));
  const numericOk = rooms === 1
    && l.size_m2 >= 14 && l.size_m2 <= 40
    && l.price >= 70000 && l.price <= 170000
    && l.faktor >= 18 && l.faktor <= 22;
  if (!numericOk) return false;
  // WGG (Wohnungsgemeinnützigkeitsgesetz) is a separate rent-control regime from
  // MRG — cost-recovery based, no computable ceiling in this pipeline (unlike MRG's
  // Richtwert). A detected WGG unit's Faktor rests on an unverifiable free-market
  // rent assumption, so it can never pass HKL regardless of the MRG check below.
  if (l.wgg_flag === true) return false;
  // is_project: a multi-unit Bauträger-project badge (immoscout24 detail page).
  // The scraped price/size behind this listing's Faktor may be one point in the
  // project's real range, not a real single unit's real price — same reasoning
  // as WGG above, this can never pass HKL regardless of the numeric bounds.
  if (l.is_project === true) return false;
  // mrg_regime/floor_cf_mo are only ever non-null when screen.py --enrich actually
  // determined baujahr (willhaben only, capped at --enrich-cap — see CLAUDE.md).
  // null means "never checked", which must FAIL this gate, not pass it by
  // default — "meets all HKL requirements" promises a verified badge, and an
  // unverified listing (the common case) isn't one. VOLLANWENDUNG (confirmed
  // pre-1953) and UNKNOWN (baujahr>=1953, MRG not ruled out) both require the
  // SAME thing: the stress-test actually ran and came back cashflow-positive.
  if (l.mrg_regime === "VOLLANWENDUNG" || l.mrg_regime === "UNKNOWN") {
    return l.floor_cf_mo != null && l.floor_cf_mo > 0;
  }
  return false;                               // NA (non-AT) / never enriched
}

// Green = positive attributes (filter TO listings that have these)
const GREEN_FLAGS = [
  { key: "Privatverkauf",         label: "Privatverkauf",         match: ["Privatverkauf"] },
  { key: "Freifläche",            label: "Freifläche",            match: ["Freifläche", "Balkon", "Loggia", "Terrasse", "Garten"] },
  { key: "Lift",                  label: "Lift",                  match: ["Lift"] },
  { key: "Fast",                  label: "Fast",                  match: [], matchFn: (l) => !!l.is_fast_deal },
  { key: "<14d",                  label: "<14d",                  match: [], matchFn: (l) => (l.days_on_market ?? Infinity) <= 14,
    description: "Max. 14 days on market" },
  { key: "MRG/Altbau",            label: "MRG/Altbau",            match: [], matchFn: (l) => l.mrg_applies === true,
    description: "AT Altbau <1953 — confirmed MRG-Vollanwendung (rent bound to the statutory Richtwert)" },
  { key: "HKL",                   label: "HKL",                   match: [], matchFn: isHklMatch,
    description: "Meets all HKL requirements: 1-Zimmer, 14–40m², €70–170k, Faktor 18–22, "
      + "MRG-checked (cashflow-positive at the Richtwert cap if Vollanwendung applies; "
      + "excluded if never enriched/unverified)" },
];

// Red = warning flags (filter OUT listings that have these)
const RED_FLAGS = [
  { key: "Altbau/MRG",           label: "Altbau/MRG",            match: ["<1953", "MRG-Richtwert", "Altbau", "MRG-FALLE"] },
  { key: "Sanierungsbedarf",      label: "Sanierungsbedarf",      match: ["Sanierungsbedarf"] },
  { key: "MRG-Falle",            label: "MRG-Falle",             match: ["MRG-FALLE"] },
  { key: "Neubau-Verdacht",       label: "Neubau-Verdacht",       match: ["Neubau-Verdacht", "Neubau-Erstbezug"] },
  { key: "HARD_NO",               label: "HARD_NO",               match: ["HARD_NO", "EG-ohne-Freifläche", "3.OG ohne Lift", "Ölheizung"] },
  { key: "Erbbaurecht",           label: "Erbbaurecht",           match: ["ERBBAURECHT", "Erbbaurecht"] },
  { key: "Leibrente",             label: "Leibrente",             match: ["LEIBRENTE"] },
  { key: "Achtung",               label: "Achtung",               match: ["⚠ Achtung"] },
  { key: "Unbefristet vermietet", label: "Unbefristet vermietet", match: ["Unbefristet vermietet"] },
  { key: "negative-cashflow",     label: "Neg. Cashflow",         match: [], matchFn: (l) => (l.monthly_cashflow || 0) < 0 },
];

const ALL_FLAGS = [...GREEN_FLAGS, ...RED_FLAGS];

// Zone-scoped loading: the corpus is fetched per zone set (server-side CSV
// filter) and merged client-side. Green is the default; red zones (~62% of
// stock) only load on demand. DATA_ANOMALY/HARD_NO docs are intentionally
// never fetched — no zone-filter option maps to them.
const ZONE_SETS = {
  green: { zones: "GREEN,GREEN_BORDER", cacheKey: "immo_listings_v3_green" },
  yellow: { zones: "YELLOW", cacheKey: "immo_listings_v3_yellow" },
  red: { zones: "RED,RED_SOFT", cacheKey: "immo_listings_v3_red" },
};
// zone-filter select value → zone-set key (ALL shows everything loaded, no fetch)
const ZONE_FILTER_TO_SET = { GREEN: "green", YELLOW: "yellow", RED: "red" };

function hasFlag(listing, flagKey) {
  const opt = ALL_FLAGS.find((o) => o.key === flagKey);
  if (!opt) return false;
  if (opt.matchFn) return opt.matchFn(listing);
  const tags = (listing.condition_tags || listing.flags || []).join(" ");
  return opt.match.some((m) => tags.includes(m));
}

function getFilterDefaults() {
  try {
    const raw = sessionStorage.getItem("immo_filters_v1");
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

export default function Listings() {
  const { t } = useI18n();
  const { aiVerdicts, listingSessions } = useOutletContext() || {};
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  const saved = getFilterDefaults();
  const [query, setQuery] = useState(saved?.query ?? "");
  const [zoneFilter, setZoneFilter] = useState(saved?.zoneFilter ?? "GREEN");
  const [gradeFilter, setGradeFilter] = useState(saved?.gradeFilter ?? "ALL");
  const [countryFilter, setCountryFilter] = useState(saved?.countryFilter ?? "AT");
  const [marketFilter, setMarketFilter] = useState(saved?.marketFilter ?? []);
  const [marketQuery, setMarketQuery] = useState(saved?.marketQuery ?? "");
  const [platformFilter, setPlatformFilter] = useState(saved?.platformFilter ?? "ALL");
  const [maxFaktor, setMaxFaktor] = useState(saved?.maxFaktor ?? "");
  const [sizeMin, setSizeMin] = useState(saved?.sizeMin ?? null);
  const [sizeMax, setSizeMax] = useState(saved?.sizeMax ?? null);
  const [priceMin, setPriceMin] = useState(saved?.priceMin ?? null);
  const [priceMax, setPriceMax] = useState(saved?.priceMax ?? null);
  const [requireFlags, setRequireFlags] = useState(saved?.requireFlags ?? []);
  const [excludeFlags, setExcludeFlags] = useState(saved?.excludeFlags ?? RED_FLAGS.map((f) => f.key));
  const [sort, setSort] = useState(saved?.sort ?? "faktor");
  // Zone sets already merged into the corpus — restored from the persisted
  // filter state so a reload rebuilds the same corpus (from the day cache,
  // without refetching). The set backing the restored zone filter is always
  // included, otherwise the restored view would be empty.
  const [loadedZones, setLoadedZones] = useState(() => {
    const restored = Array.isArray(saved?.loadedZones)
      ? saved.loadedZones.filter((k) => ZONE_SETS[k])
      : [];
    const initial = new Set(restored.length ? restored : ["green"]);
    const filterSet = ZONE_FILTER_TO_SET[saved?.zoneFilter];
    if (filterSet) initial.add(filterSet);
    return [...initial];
  });
  const [zoneLoading, setZoneLoading] = useState(false);
  const nav = useNavigate();
  const mountedRef = useRef(false);
  // loadedRef tracks sets ACTUALLY merged (starts empty — loadedZones state
  // is the intended set list for the mount load, the ref gates re-entry).
  const loadedRef = useRef(new Set());
  const inflightRef = useRef(new Set());

  const loadZoneSet = useCallback(async (setKey) => {
    if (loadedRef.current.has(setKey) || inflightRef.current.has(setKey)) return;
    inflightRef.current.add(setKey);
    setZoneLoading(true);
    const setDef = ZONE_SETS[setKey];
    try {
      let docs = await getDayCache(setDef.cacheKey);
      if (!docs) {
        const { data } = await api.get(`/listings?zone=${setDef.zones}&limit=20000`);
        docs = data.listings || [];
        setDayCache(setDef.cacheKey, docs);
      }
      // Defensive dedupe by id — a listing could theoretically ride in two
      // zone responses; the corpus must not double-count it.
      setAll((prev) => {
        const seen = new Set(prev.map((l) => l.id));
        return [...prev, ...docs.filter((l) => !seen.has(l.id))];
      });
      loadedRef.current.add(setKey);
      setLoadedZones([...loadedRef.current]);
    } catch (e) {
      console.error(`Listings zone load failed (${setKey})`, e);
    } finally {
      inflightRef.current.delete(setKey);
      setZoneLoading(inflightRef.current.size > 0);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all(loadedZones.map((k) => loadZoneSet(k)));
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRequire = (key) =>
    setRequireFlags((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const toggleExclude = (key) =>
    setExcludeFlags((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const clearFlags = () => { setRequireFlags([]); setExcludeFlags([]); };

  // Persist filter state across navigation
  useEffect(() => {
    try {
      sessionStorage.setItem("immo_filters_v1", JSON.stringify({
        query, zoneFilter, gradeFilter, countryFilter, marketFilter, marketQuery,
        platformFilter, maxFaktor, sizeMin, sizeMax, priceMin, priceMax, requireFlags, excludeFlags, sort, loadedZones,
      }));
    } catch {}
  }, [query, zoneFilter, gradeFilter, countryFilter, marketFilter, marketQuery, platformFilter, maxFaktor, sizeMin, sizeMax, priceMin, priceMax, requireFlags, excludeFlags, sort, loadedZones]);

  // Reset market filter when country changes — skip the initial mount to preserve restored state
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    setMarketFilter([]);
    setMarketQuery("");
  }, [countryFilter]);

  // Derive market options from listings that match the current country selection
  const marketOptions = useMemo(() => {
    return buildMarketOptions(all, countryFilter);
  }, [all, countryFilter]);

  const filteredMarketOptions = useMemo(() => {
    const q = normalizeMarketSearch(marketQuery);
    if (!q) return marketOptions;
    return marketOptions.filter((opt) => opt.searchText.includes(q));
  }, [marketOptions, marketQuery]);

  // Slider-Bounds aus bereinigten Werten: der Korpus enthält dokumentierten
  // Datenmüll (Größen-Tipfehler wie 890.000 m², vgl. PER_M2_RANGE-Kommentar in
  // invest_config.py) — ein rohes Math.max ließe beide Regler bei ~1,15 Mio
  // enden und den relevanten ETW-Bereich unbedienbar machen. Darum: nur Werte
  // innerhalb fachlicher Sanity-Grenzen, und das Obere Ende als 99. Perzentil
  // statt Max, damit auch legitime Ausreißer (Zinshaus, Villa) den Regler
  // nicht sprengen. Die Listings selbst bleiben unverändert sichtbar — die
  // Bounds wirken nur auf die Regler-Endpunkte.
  const sizeBounds = useMemo(() => {
    const values = all
      .map((l) => Number(l.size_m2))
      .filter((n) => Number.isFinite(n) && n >= SIZE_SANITY.min && n <= SIZE_SANITY.max)
      .sort((a, b) => a - b);
    return {
      min: values.length ? Math.floor(values[0]) : 0,
      max: values.length ? Math.ceil(percentile99(values)) : 150,
    };
  }, [all]);

  const priceBounds = useMemo(() => {
    const values = all
      .map((l) => Number(l.price))
      .filter((n) => Number.isFinite(n) && n >= PRICE_SANITY.min && n <= PRICE_SANITY.max)
      .sort((a, b) => a - b);
    return {
      min: values.length ? Math.floor(values[0]) : 0,
      max: values.length ? Math.ceil(percentile99(values)) : 1000000,
    };
  }, [all]);

  useEffect(() => {
    if (!all.length) return;
    setSizeMin((prev) => (prev == null ? sizeBounds.min : prev));
    setSizeMax((prev) => (prev == null ? sizeBounds.max : prev));
    setPriceMin((prev) => (prev == null ? priceBounds.min : prev));
    setPriceMax((prev) => (prev == null ? priceBounds.max : prev));
  }, [all.length, sizeBounds.min, sizeBounds.max, priceBounds.min, priceBounds.max]);

  const sizeMinValue = sizeMin != null ? Math.max(sizeBounds.min, Number(sizeMin)) : sizeBounds.min;
  const sizeMaxValue = sizeMax != null ? Math.min(sizeBounds.max, Number(sizeMax)) : sizeBounds.max;
  const priceMinValue = priceMin != null ? Math.max(priceBounds.min, Number(priceMin)) : priceBounds.min;
  const priceMaxValue = priceMax != null ? Math.min(priceBounds.max, Number(priceMax)) : priceBounds.max;
  const effectiveSizeMin = Math.min(sizeMinValue, sizeMaxValue);
  const effectiveSizeMax = Math.max(sizeMinValue, sizeMaxValue);
  const effectivePriceMin = Math.min(priceMinValue, priceMaxValue);
  const effectivePriceMax = Math.max(priceMinValue, priceMaxValue);

  const rows = useMemo(() => {
    let r = [...all];
    if (zoneFilter !== "ALL") {
      const groups = { GREEN: ["GREEN", "GREEN_BORDER"], YELLOW: ["YELLOW"], RED: ["RED", "RED_SOFT"] };
      r = r.filter((l) => groups[zoneFilter].includes(l.zone));
    }
    if (gradeFilter !== "ALL") r = r.filter((l) => l.lage_grade === gradeFilter);
    if (countryFilter !== "ALL") r = r.filter((l) => l.country === countryFilter);
    if (marketFilter.length > 0) r = r.filter((l) => marketOptionMatchesSelection(l, marketFilter));
    if (platformFilter !== "ALL") r = r.filter((l) => l.platform === platformFilter);
    if (effectiveSizeMin > sizeBounds.min || effectiveSizeMax < sizeBounds.max) {
      r = r.filter((l) => {
        const size = l.size_m2 ?? Infinity;
        return size >= effectiveSizeMin && size <= effectiveSizeMax;
      });
    }
    if (effectivePriceMin > priceBounds.min || effectivePriceMax < priceBounds.max) {
      r = r.filter((l) => {
        const price = l.price ?? Infinity;
        return price >= effectivePriceMin && price <= effectivePriceMax;
      });
    }
    if (maxFaktor) {
      const mf = parseFloat(maxFaktor);
      if (!isNaN(mf)) r = r.filter((l) => l.faktor <= mf);
    }
    // green: must have ALL required flags (AND, not OR)
    if (requireFlags.length > 0) {
      r = r.filter((l) => requireFlags.every((key) => hasFlag(l, key)));
    }
    // red: must not have any excluded flag
    if (excludeFlags.length > 0) {
      r = r.filter((l) => !excludeFlags.some((key) => hasFlag(l, key)));
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter((l) =>
        l.market_name.toLowerCase().includes(q) ||
        l.address.toLowerCase().includes(q) ||
        l.id.toLowerCase().includes(q)
      );
    }
    const sortMap = {
      faktor: (a, b) => a.faktor - b.faktor,
      price: (a, b) => a.price - b.price,
      price_per_sqm: (a, b) => (a.price_per_sqm ?? Infinity) - (b.price_per_sqm ?? Infinity),
      size: (a, b) => a.size_m2 - b.size_m2,
      baujahr_asc: (a, b) => (a.baujahr ?? Infinity) - (b.baujahr ?? Infinity),
      baujahr_desc: (a, b) => (b.baujahr ?? -Infinity) - (a.baujahr ?? -Infinity),
      cashflow: (a, b) => b.monthly_cashflow - a.monthly_cashflow,
      vs_markt: (a, b) => (a.vs_markt_pct ?? Infinity) - (b.vs_markt_pct ?? Infinity),
      newest: (a, b) => a.days_on_market - b.days_on_market,
    };
    r.sort(sortMap[sort]);
    return r;
  }, [all, query, zoneFilter, gradeFilter, countryFilter, marketFilter, platformFilter, effectiveSizeMin, effectiveSizeMax, effectivePriceMin, effectivePriceMax, sizeBounds.min, sizeBounds.max, priceBounds.min, priceBounds.max, maxFaktor, requireFlags, excludeFlags, sort]);

  if (loading) return <div className="p-10"><Spinner /></div>;

  const exportShortlist = async () => {
    const ids = rows.map((r) => r.id);
    const res = await api.post(
      "/export/shortlist.md",
      { listing_ids: ids, title: "immo-watch — filtered shortlist" },
      { responseType: "blob" }
    );
    const blob = new Blob([res.data], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "immo-watch-shortlist.md"; a.click();
    URL.revokeObjectURL(url);
  };

  const anyFlagActive = requireFlags.length > 0 || excludeFlags.length > 0;

  return (
    <div className="reveal">
      {/* header */}
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline">{t("listings.eyebrow")}</div>
            <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("listings.title")}</h1>
            <p className="mt-2 text-xs text-text-muted">{t("listings.countLabel", { shown: rows.length, total: all.length })}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={exportShortlist}
              data-testid="export-shortlist"
              className="inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest2 border border-zinc-700 text-text-secondary hover:border-white hover:text-white"
            >
              <FileDown size={14} /> {t("listings.export", { count: rows.length })}
            </button>
            <MockBadge />
          </div>
        </div>
      </div>

      {/* filter row 1 */}
      <div className="grid grid-cols-12 border-b border-border" data-testid="listings-filters">
        <div className="col-span-12 md:col-span-2 p-4 border-r border-border flex items-center gap-2">
          <Search size={14} className="text-text-muted" />
          <Input
            data-testid="listings-search"
            placeholder={t("common.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="!border-none !px-0"
          />
        </div>
        <FilterCell label={t("listings.zone")}>
          <select
            data-testid="filter-zone"
            value={zoneFilter}
            onChange={(e) => {
              const v = e.target.value;
              setZoneFilter(v);
              // Activating a zone lazily loads its set into the corpus;
              // switching away only re-filters client-side, nothing unloads.
              const setKey = ZONE_FILTER_TO_SET[v];
              if (setKey) loadZoneSet(setKey);
            }}
            className="bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white"
          >
            <option value="ALL">{t("common.all")}</option><option value="GREEN">GREEN</option><option value="YELLOW">YELLOW</option><option value="RED">RED</option>
          </select>
          {zoneLoading && (
            <span data-testid="zone-loading" className="ml-2 text-[10px] font-mono uppercase tracking-widest text-text-muted">
              {t("listings.loadingZones")}
            </span>
          )}
        </FilterCell>
        <FilterCell label={t("listings.lage")}>
          <select data-testid="filter-lage" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white">
            <option value="ALL">{t("common.all")}</option><option>A</option><option>B+</option><option>B</option><option>B-</option>
          </select>
        </FilterCell>
        <FilterCell label={t("listings.country")}>
          <select data-testid="filter-country" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white">
            <option value="ALL">{t("common.all")}</option><option value="AT">AT</option><option value="DE">DE</option>
          </select>
        </FilterCell>
        <FilterCell label={t("listings.market")}>
          <MarketMultiSelect
            options={filteredMarketOptions}
            value={marketFilter}
            onChange={setMarketFilter}
            t={t}
            query={marketQuery}
            onQueryChange={setMarketQuery}
          />
        </FilterCell>
        <FilterCell label={t("listings.portal")}>
          <select data-testid="filter-platform" value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} className="bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white">
            <option value="ALL">{t("common.all")}</option>
            {PORTAL_PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {platform}
              </option>
            ))}
          </select>
        </FilterCell>
        <FilterCell label={t("listings.maxFaktor")}>
          <Input data-testid="filter-max-faktor" value={maxFaktor} onChange={(e) => setMaxFaktor(e.target.value)} placeholder="26" className="!py-1.5 !text-xs" />
        </FilterCell>
        <FilterCell label={t("listings.sortBy")} last>
          <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-bg border border-border text-xs font-mono px-2 py-1.5 text-white">
            <option value="faktor">{t("listings.sortFaktor")}</option><option value="cashflow">{t("listings.sortCashflow")}</option><option value="vs_markt">{t("listings.sortVsMarkt")}</option><option value="price">{t("listings.sortPrice")}</option><option value="price_per_sqm">€/m² ↑</option><option value="size">m² ↑</option><option value="baujahr_asc">{t("listings.sortBaujahrAsc")}</option><option value="baujahr_desc">{t("listings.sortBaujahrDesc")}</option><option value="newest">{t("listings.sortNewest")}</option>
          </select>
        </FilterCell>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 border-b border-border">
        <RangeCell
          label="m² range"
          minValue={sizeMinValue}
          maxValue={sizeMaxValue}
          min={sizeBounds.min}
          max={sizeBounds.max}
          step={1}
          testidMin="filter-size-min"
          testidMax="filter-size-max"
          onMinChange={setSizeMin}
          onMaxChange={setSizeMax}
          minLabel={`${sizeMinValue} m²`}
          maxLabel={`${sizeMaxValue} m²`}
        />
        <RangeCell
          label="Preis range"
          minValue={priceMinValue}
          maxValue={priceMaxValue}
          min={priceBounds.min}
          max={priceBounds.max}
          step={5000}
          testidMin="filter-price-min"
          testidMax="filter-price-max"
          onMinChange={setPriceMin}
          onMaxChange={setPriceMax}
          minLabel={`€${Math.round(priceMinValue).toLocaleString()}`}
          maxLabel={`€${Math.round(priceMaxValue).toLocaleString()}`}
          last
        />
      </div>

      {/* filter row 2 — flags */}
      <div className="border-b border-border">
        {/* green flags: require */}
        <div className="px-4 pt-3 pb-2 flex items-start gap-3 flex-wrap">
          <span className="overline text-signal-green pt-0.5 shrink-0 w-16">{t("listings.require")}</span>
          <div className="flex flex-wrap gap-1.5">
            {GREEN_FLAGS.map((opt) => {
              const active = requireFlags.includes(opt.key);
              return (
                <button key={opt.key} onClick={() => toggleRequire(opt.key)} title={opt.description}
                  className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
                    active ? "border-signal-green text-signal-green bg-signal-green/10" : "border-border text-text-muted hover:border-signal-green/50 hover:text-signal-green/70"
                  }`}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        {/* red flags: exclude */}
        <div className="px-4 pb-3 pt-1 flex items-start gap-3 flex-wrap">
          <span className="overline text-signal-red pt-0.5 shrink-0 w-16">{t("listings.exclude")}</span>
          <div className="flex flex-wrap gap-1.5">
            {RED_FLAGS.map((opt) => {
              const active = excludeFlags.includes(opt.key);
              return (
                <button key={opt.key} onClick={() => toggleExclude(opt.key)}
                  className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
                    active ? "border-signal-red text-signal-red bg-signal-red/10" : "border-border text-text-muted hover:border-signal-red/50 hover:text-signal-red/70"
                  }`}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          {anyFlagActive && (
            <button onClick={clearFlags} className="flex items-center gap-1 text-[10px] font-mono text-text-muted hover:text-white ml-1 pt-1">
              <X size={10} /> {t("listings.clearFlags")}
            </button>
          )}
        </div>
      </div>

      {/* grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-border border-b border-border">
        {rows.map((l) => (
          <ListingCard key={l.id} l={l} onClick={() => nav(`/listings/${l.id}`)} t={t}
            aiVerdict={aiVerdicts?.[l.id]?.verdict}
            hasSession={!!listingSessions?.[l.id]}
          />
        ))}
        {rows.length === 0 && all.length === 0 && (
          <div className="col-span-3 p-10 text-center text-text-muted font-mono text-xs">
            {t("listings.noRealData")} <code className="text-white">push_to_web.py</code>
            {" "}{t("listings.noRealData2")}
          </div>
        )}
        {rows.length === 0 && all.length > 0 && (
          <div className="col-span-3 p-10 text-center text-text-muted font-mono text-xs">
            {t("listings.noMatch")}
          </div>
        )}
      </div>
    </div>
  );
}

const AI_VERDICT_STYLE = {
  INTERESTING: "text-signal-green border-signal-green/50",
  WATCH: "text-signal-yellow border-signal-yellow/50",
  PASS: "text-signal-red border-signal-red/40",
};
const AI_VERDICT_LABEL = { INTERESTING: "AI:✓", WATCH: "AI:~", PASS: "AI:✕" };

function ListingCard({ l, onClick, t, aiVerdict, hasSession }) {
  const flagStr = (l.condition_tags || l.flags || []).join(" ");
  const hasWarning = ["⚠", "HARD_NO", "FLAG:"].some((s) => flagStr.includes(s));

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") onClick();
        else if (e.key === " ") { e.preventDefault(); onClick(); }
      }}
      role="button"
      tabIndex={0}
      data-testid={`listing-card-${l.id}`}
      className="bg-bg hover:bg-surfaceHover text-left p-5 transition-colors group relative cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <ZoneChip zone={l.zone} />
          <LageBadge grade={l.lage_grade} />
          {l.is_fast_deal && (
            <span title={t("listings.fastDealTooltip")} className="text-[9px] font-mono text-orange-400 border border-orange-400/50 px-1.5 py-0.5 tracking-widest2">
              {l.is_new ? "⚡ NEW" : "FAST"}
            </span>
          )}
          {(l.flip_verdict === "buy" || l.flip_verdict === "negotiate") && (
            <span
              title={t("listings.flipChipTooltip", {
                max: `€${Math.round((l.flip_max_ankauf ?? 0) / 1000)}k`,
                pct: l.flip_diff_pct,
              })}
              className={`text-[9px] font-mono px-1.5 py-0.5 tracking-widest2 border ${
                l.flip_verdict === "buy"
                  ? "text-signal-green border-signal-green/50"
                  : "text-signal-yellow border-signal-yellow/50"
              }`}
            >
              FF {l.flip_diff_pct}%
            </span>
          )}
          {hasWarning && (
            <span title={t("listings.warningFlagTooltip")} className="text-[9px] font-mono text-signal-red border border-signal-red/40 px-1.5 py-0.5 tracking-widest2">
              ⚠
            </span>
          )}
          {aiVerdict && (
            <span title={`AI assessment: ${aiVerdict}`} className={`text-[9px] font-mono border px-1.5 py-0.5 tracking-widest2 ${AI_VERDICT_STYLE[aiVerdict]}`}>
              {AI_VERDICT_LABEL[aiVerdict]}
            </span>
          )}
          {hasSession && !aiVerdict && (
            <span title="Saved AI conversation" className="text-[9px] font-mono text-text-muted border border-border px-1.5 py-0.5">AI</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="overline">{l.platform}</span>
          {l.url && (
            <a
              href={l.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              title={t("listings.openSource")}
              data-testid={`listing-card-source-${l.id}`}
              className="text-text-muted hover:text-white transition-colors"
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs text-text-muted font-mono">{l.market_name} · {l.country}</div>
        <div className="mt-1 font-heading text-lg leading-tight">{l.title}</div>
        <div className="text-[11px] text-text-muted truncate mt-0.5">{l.address}</div>
      </div>

      <div className="mt-5 grid grid-cols-5 gap-3 border-t border-border pt-4">
        <Metric label={t("dashboard.colFaktor")} value={(l.faktor ?? 0).toFixed(1)} accent={l.zone.includes("GREEN") ? "green" : l.zone === "YELLOW" ? "yellow" : "red"} />
        <Metric label={t("listings.price")} value={`€${((l.price ?? 0) / 1000).toFixed(0)}k`} />
        {/* Baujahr on the same level as price: MRG-Vollanwendung (AT Altbau
            <1953, Richtwert-bound rent) is flagged inline via mrg_applies. */}
        <Metric
          label={t("listings.baujahrMetric")}
          value={
            l.baujahr != null ? (
              <span className="inline-flex items-center gap-1.5">
                {l.baujahr}
                {l.mrg_applies === true && (
                  <span
                    title={t("listings.mrgBadgeTooltip")}
                    className="text-[9px] font-mono text-signal-yellow border border-signal-yellow/50 px-1 py-px tracking-widest2"
                  >
                    {t("listings.mrgBadge")}
                  </span>
                )}
              </span>
            ) : "—"
          }
        />
        <Metric label="€/m²" value={(l.price_per_sqm ?? 0).toLocaleString()} />
        <Metric label={t("dashboard.colCashflow")} value={l.monthly_cashflow != null ? `${l.monthly_cashflow >= 0 ? "+" : ""}€${l.monthly_cashflow}` : "—"} accent={(l.monthly_cashflow ?? 0) >= 0 ? "green" : "red"} />
      </div>

      <div className="mt-4 flex items-center justify-between text-[10px] font-mono">
        {/* Low-confidence deltas are muted, not hidden: the comparables behind
            them scatter too much to read the sign as a signal. */}
        <span
          className={isLowConfidence(l)
            ? "text-text-muted"
            : (l.vs_markt_pct ?? 0) < 0 ? "text-signal-green" : "text-signal-red"}
          title={l.mv_spread != null
            ? t("listings.mvSpreadTooltip", {
                spread: l.mv_spread.toFixed(2),
                band: l.mv_band_used || "—",
              })
              + (l.mv_spread_from
                  ? ` — ${t("listings.mvSpreadInherited", { band: l.mv_spread_from })}`
                  : "")
              + (isLowConfidence(l) ? ` — ${t("listings.mvLowConfidence")}` : "")
            : undefined}
        >
          {t("listings.vsMarkt")} {l.vs_markt_pct != null ? `${l.vs_markt_pct > 0 ? "+" : ""}${l.vs_markt_pct}%` : "—"}
          {isLowConfidence(l) && " ?"}
        </span>
        <span className="text-text-muted">{t("listings.cond")} {l.condition_score}</span>
        <span className="text-text-muted">{l.days_on_market != null ? `${l.days_on_market}d` : "—"}</span>
        <span className="text-text-muted">{l.size_m2}m² · {l.rooms}Zi</span>
      </div>
    </div>
  );
}
